/**
 * Home's suggested starts above the composer, built from what is on
 * this person's Home right now. Rules, not a model: each comes from a real
 * item (an assigned finding, a review date, an update to assess, a failed
 * run), so it is instant, free, private and never invents work. Ranked by
 * urgency, at most two of a kind, three shown. The chip names the verb and
 * the item's own title (`suggestionLabel`) until the server's written label
 * for it lands (useHomeSuggestions); picking it fills the composer with the
 * full ask, never the label, which the person reads and sends.
 *
 * @module components/home/homeSuggestions.logic
 */
import type { HomeSuggestionKind, HomeSuggestionSubject } from "@amoreg/contracts";
import { homeSuggestionSubjectKey, SUGGESTION_VERB } from "@amoreg/shared/homeSuggestionLabels";

import type { FindingItem, Severity } from "../regulation/finding/findingItem";
import { daysUntil, preparePrompt, relativeDue } from "./HomeAgenda.logic";
import { isUrgentFinding } from "./HomeFindings.logic";
import { findingDueAt, isFindingOverdue, type HomeGlance } from "./homeGlance.logic";
import { comingUpName, compactAge, type HomeComingUpItem } from "./homeLogic";

export type SuggestionKind = HomeSuggestionKind;

/** A suggestion before its label is written. */
export interface SuggestionDraft {
  readonly key: string;
  readonly rank: number;
  readonly kind: SuggestionKind;
  /** The row it stands for, which the server reads to write its label. */
  readonly subject: HomeSuggestionSubject;
  /** A finding's chip shows its severity; the others their kind's icon. */
  readonly severity: Severity | null;
  readonly verb: string;
  readonly object: string;
  /** The tooltip's first line: why now, and where. */
  readonly context: string;
  readonly urgent: boolean;
  /** What picking the chip puts in the composer. */
  readonly prompt: string;
}

export interface Suggestion extends SuggestionDraft {
  /** The chip's text. */
  readonly label: string;
}

/** Writes a suggestion's chip text from its draft: `suggestionLabel` unless a caller supplies another. */
export type SuggestionLabeler = (suggestion: SuggestionDraft) => string;

/** The verb and the item's own title, whole: the chip cuts a long one with an ellipsis. */
export const suggestionLabel: SuggestionLabeler = (suggestion) => `${suggestion.verb}: ${suggestion.object}`;

/** The label written for a suggestion's subject, else `suggestionLabel`. */
export const writtenLabeler =
  (written: ReadonlyMap<string, string>): SuggestionLabeler =>
  (suggestion) =>
    written.get(homeSuggestionSubjectKey(suggestion.subject)) ?? suggestionLabel(suggestion);

export const SUGGESTIONS_SHOWN = 3;
const PER_KIND = 2;

function findingSuggestion(item: FindingItem, rank: number, now: number): SuggestionDraft {
  const at = findingDueAt(item);
  const overdue = isFindingOverdue(item, now);
  const when = Number.isFinite(at)
    ? overdue
      ? relativeDue(at, now)
      : `Due ${relativeDue(at, now)}`
    : `Raised ${compactAge(new Date(item.raisedAt).toISOString(), now)} ago`;
  return {
    key: `finding:${item.id}`,
    rank,
    kind: "finding",
    subject: { kind: "finding", source: item.origin === "finding" ? "assessment" : "recommendation", id: item.id },
    severity: item.severity,
    verb: SUGGESTION_VERB.finding,
    object: item.title,
    context: `${when} · ${item.documentName}`,
    urgent: overdue,
    prompt: `Help me resolve the finding “${item.title}” in ${item.documentName}: what has to change, and draft the amendment.`,
  };
}

export function buildSuggestions(
  input: {
    readonly findings: ReadonlyArray<FindingItem>;
    readonly comingUp: ReadonlyArray<HomeComingUpItem>;
    readonly glance: Pick<HomeGlance, "mine" | "toReview" | "failing">;
    readonly now: number;
  },
  label: SuggestionLabeler = suggestionLabel,
): ReadonlyArray<Suggestion> {
  const { findings, comingUp, glance, now } = input;
  const mine = glance.mine;
  const mineRanked = mine.map((item) => {
    const at = findingDueAt(item);
    const days = Number.isFinite(at) ? daysUntil(at, now) : Number.POSITIVE_INFINITY;
    return findingSuggestion(item, days < 0 ? 100 : days <= 7 ? 80 : 45, now);
  });
  const severe =
    mine.length < 2
      ? findings
          .filter((item) => isUrgentFinding(item) && !mine.includes(item))
          // Critical first (a stable sort keeps the rest in order), so a critical is never cut for two highs.
          .toSorted((a, b) => Number(b.severity === "critical") - Number(a.severity === "critical"))
          .slice(0, 2)
          .map((item) => findingSuggestion(item, item.severity === "critical" ? 55 : 40, now))
      : [];
  const updates = glance.toReview.slice(0, 2).map(
    (update): SuggestionDraft => ({
      key: `update:${update.id}`,
      rank: 70,
      kind: "update",
      subject: { kind: "update", id: update.id },
      severity: null,
      verb: SUGGESTION_VERB.update,
      object: update.title,
      context: `${update.jurisdictionName} · spotted ${compactAge(update.detectedAt, now)} ago`,
      urgent: false,
      prompt: `What does “${update.title}” (${update.jurisdictionName}) mean for us, and which of our policies does it touch?`,
    }),
  );
  const workflows = glance.failing.slice(0, 1).map((workflow): SuggestionDraft => {
    // Failing by its schedule alone: the schedule could not start a run, which is not a failed run.
    const scheduleOnly = workflow.lastRun?.status !== "failed" && Boolean(workflow.schedule?.lastError);
    return {
      key: `workflow:${workflow.id}`,
      rank: 65,
      kind: "workflow",
      subject: { kind: "workflow", id: workflow.id },
      severity: null,
      verb: SUGGESTION_VERB.workflow,
      object: workflow.name,
      context: scheduleOnly ? "Schedule could not start a run" : "Last run failed",
      urgent: true,
      prompt: scheduleOnly
        ? `Why could the “${workflow.name}” workflow's schedule not start a run, and how do I fix it?`
        : `Why did the “${workflow.name}” workflow fail on its last run, and how do I fix it?`,
    };
  });
  const reviews = comingUp.slice(0, 2).map((item): SuggestionDraft => {
    const days = daysUntil(Date.parse(item.at), now);
    return {
      key: `review:${item.id}`,
      rank: days < 0 ? 90 : days <= 14 ? 60 : days <= 45 ? 50 : 20,
      kind: "review",
      subject: item.policyId
        ? { kind: "review", source: "policy", id: item.policyId }
        : { kind: "review", source: "change", id: item.updateId ?? "" },
      severity: null,
      verb: SUGGESTION_VERB.review,
      object: comingUpName(item),
      // A review: "Annual review · in 3 days"; a publication: "Isle of Man · Takes effect in 3 days".
      context:
        item.kind === "review"
          ? `${item.title} · ${relativeDue(Date.parse(item.at), now)}`
          : `${item.context ? `${item.context} · ` : ""}${item.detail} ${relativeDue(Date.parse(item.at), now)}`,
      urgent: item.overdue,
      prompt: preparePrompt(item),
    };
  });
  const seen = new Map<SuggestionKind, number>();
  return [...mineRanked, ...severe, ...updates, ...workflows, ...reviews]
    .toSorted((a, b) => b.rank - a.rank)
    .filter((suggestion) => {
      const count = seen.get(suggestion.kind) ?? 0;
      seen.set(suggestion.kind, count + 1);
      return count < PER_KIND;
    })
    .slice(0, SUGGESTIONS_SHOWN)
    .map((suggestion) => ({ ...suggestion, label: label(suggestion) }));
}
